import React, { useRef, useState, useEffect } from 'react';

const RecordingPlayer = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [duration, setDuration] = useState("00:00");

  useEffect(() => {
    const audio = audioRef.current;
    
    const updateProgress = () => {
      if (audio && !isNaN(audio.duration)) {
        const currentTime = audio.currentTime;
        const duration = audio.duration;
        const progress = (currentTime / duration) * 100;
        setProgress(progress);
        setCurrentTime(formatTime(currentTime));
        setDuration(formatTime(duration));
      }
    };

    const handleLoadedMetadata = () => {
      if (audio && !isNaN(audio.duration)) {
        setDuration(formatTime(audio.duration));
      }
    };

    const handleError = (error) => {
      console.error('Audio loading error:', error);
      setIsPlaying(false);
    };

    if (audio) {
      audio.addEventListener("timeupdate", updateProgress);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("error", handleError);

      return () => {
        audio.removeEventListener("timeupdate", updateProgress);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("error", handleError);
      };
    }
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.error('Playback failed:', error);
          setIsPlaying(false);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "00:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="w-full max-w-md mx-auto p-4 bg-white rounded-lg shadow">
      <audio 
        ref={audioRef} 
        src={audioUrl}
        preload="metadata"
        className="hidden"
      />
      
      {/* Progress Bar */}
      <div className="w-full h-2 bg-gray-200 rounded-full mb-2">
        <div 
          className="h-full bg-blue-500 rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }} 
        />
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-between px-2">
        <span className="text-sm text-gray-600 font-mono">{currentTime}</span>
        <button 
          onClick={togglePlay} 
          className={`
            px-4 py-2 rounded-full
            ${!audioUrl 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700'}
            text-white font-medium transition-colors
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
          `}
          disabled={!audioUrl}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>
        <span className="text-sm text-gray-600 font-mono">{duration}</span>
      </div>
    </div>
  );
};

export default RecordingPlayer;